import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const CargoTab = ({ data, onChange }) => {
    const emptyRow = () => ({ CargoDescriptionID: null, Mark: '', Description: '', Weight: 0, Volume: 0, Packages: 0, GrossWeight: 0 });

    const [cargoItems, setCargoItems] = useState(data?.length ? data : [emptyRow()]);

    useEffect(() => {
        if (!data || data.length === 0) {
            const def = [emptyRow()];
            setCargoItems(def);
            onChange(def);
        } else {
            setCargoItems(data);
        }
    }, [data]);

    const addNewRow = () => {
        const updated = [...cargoItems, emptyRow()];
        setCargoItems(updated); onChange(updated);
    };

    const deleteRow = (index) => {
        if (cargoItems.length === 1) { alert('Cannot delete the last row. At least one cargo description is required.'); return; }
        if (window.confirm('Delete this cargo description?')) {
            const updated = cargoItems.filter((_, i) => i !== index);
            setCargoItems(updated); onChange(updated);
        }
    };

    const updateRow = (index, field, value) => {
        const updated = [...cargoItems];
        updated[index][field] = value;
        setCargoItems(updated); onChange(updated);
    };

    const totals = cargoItems.reduce((acc, item) => ({
        Weight:      acc.Weight      + (parseFloat(item.Weight)      || 0),
        Volume:      acc.Volume      + (parseFloat(item.Volume)      || 0),
        Packages:    acc.Packages    + (parseFloat(item.Packages)    || 0),
        GrossWeight: acc.GrossWeight + (parseFloat(item.GrossWeight) || 0),
    }), { Weight: 0, Volume: 0, Packages: 0, GrossWeight: 0 });

    const cellInput  = `w-full px-2.5 py-2 border border-[#CBD5E1] rounded text-sm outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] font-[inherit]`;
    const numInput   = cellInput + ' text-right font-mono tabular-nums';
    const textareaCls = `w-full min-h-[56px] px-2.5 py-2 border border-[#CBD5E1] rounded text-sm resize-y outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] font-[inherit]`;

    const summaryItems = [
        { label: 'Total Weight',       value: `${totals.Weight.toFixed(2)} kg`       },
        { label: 'Total Volume',       value: `${totals.Volume.toFixed(3)} CBM`      },
        { label: 'Total Packages',     value: `${totals.Packages}`                   },
        { label: 'Total Gross Weight', value: `${totals.GrossWeight.toFixed(2)} kg`  },
    ];

    return (
        <div className="p-5 bg-white rounded-lg flex flex-col h-full gap-4">

            {/* Toolbar */}
            <div className="flex items-center justify-between flex-shrink-0">
                <h3 className="text-base font-semibold text-gray-800 m-0">Cargo Descriptions</h3>
                <button onClick={addNewRow}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5"
                    style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                    <Plus size={15} /> Add Cargo Item
                </button>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-auto border border-[#E2E8F0] rounded-lg">
                <table className="w-full border-collapse text-sm min-w-[800px]">
                    <thead className="sticky top-0 z-10"
                        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                        <tr>
                            {[
                                { label: '#',             w: 'w-10'   },
                                { label: 'Mark',          w: 'w-36'   },
                                { label: 'Description',   w: 'min-w-[220px]' },
                                { label: 'Weight (kg)',   w: 'w-28'   },
                                { label: 'Volume (CBM)',  w: 'w-28'   },
                                { label: 'Packages',      w: 'w-24'   },
                                { label: 'Gross Weight',  w: 'w-28'   },
                                { label: 'Action',        w: 'w-16'   },
                            ].map(({ label, w }) => (
                                <th key={label} className={`${w} px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-white border-r border-white/20 last:border-r-0`}>
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {cargoItems.length === 0 ? (
                            <tr><td colSpan="8" className="py-10 text-center text-gray-400 text-sm">No cargo items. Click "Add Cargo Item" to begin.</td></tr>
                        ) : (
                            cargoItems.map((item, index) => (
                                <tr key={index} className="border-b border-gray-100 hover:bg-[#EFF6FF] transition-colors duration-150">
                                    <td className="px-3 py-2.5 text-center font-bold text-[#2563EB]">{index + 1}</td>
                                    <td className="px-2 py-2">
                                        <input type="text" value={item.Mark || ''} placeholder="Mark"
                                            onChange={e => updateRow(index, 'Mark', e.target.value)} className={cellInput} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <textarea value={item.Description || ''} rows={2} placeholder="Enter cargo description"
                                            onChange={e => updateRow(index, 'Description', e.target.value)} className={textareaCls} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" value={item.Weight || ''} step="0.01" placeholder="0.00"
                                            onChange={e => updateRow(index, 'Weight', parseFloat(e.target.value) || 0)} className={numInput} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" value={item.Volume || ''} step="0.001" placeholder="0.000"
                                            onChange={e => updateRow(index, 'Volume', parseFloat(e.target.value) || 0)} className={numInput} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" value={item.Packages || ''} step="1" placeholder="0"
                                            onChange={e => updateRow(index, 'Packages', parseFloat(e.target.value) || 0)} className={numInput} />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input type="number" value={item.GrossWeight || ''} step="0.01" placeholder="0.00"
                                            onChange={e => updateRow(index, 'GrossWeight', parseFloat(e.target.value) || 0)} className={numInput} />
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                        <button onClick={() => deleteRow(index)} title="Delete"
                                            disabled={cargoItems.length === 1}
                                            className="p-1.5 rounded border-0 cursor-pointer bg-transparent transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
                                            style={{ color: '#DC2626' }}
                                            onMouseEnter={(e) => { if (cargoItems.length !== 1) { e.currentTarget.style.backgroundColor = '#FEE2E2'; e.currentTarget.style.color = '#B91C1C'; } }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#DC2626'; }}>
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                        <tr>
                            <th colSpan="3" className="px-3 py-3 text-left text-sm font-bold text-gray-700">Total:</th>
                            <th className="px-3 py-3 text-right font-mono text-sm font-bold" style={{ color: '#15803D' }}>{totals.Weight.toFixed(2)}</th>
                            <th className="px-3 py-3 text-right font-mono text-sm font-bold" style={{ color: '#15803D' }}>{totals.Volume.toFixed(3)}</th>
                            <th className="px-3 py-3 text-right font-mono text-sm font-bold" style={{ color: '#15803D' }}>{totals.Packages}</th>
                            <th className="px-3 py-3 text-right font-mono text-sm font-bold" style={{ color: '#15803D' }}>{totals.GrossWeight.toFixed(2)}</th>
                            <th></th>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Summary cards */}
            <div className="flex-shrink-0">
                <div className="flex flex-wrap gap-3">
                    {summaryItems.map(({ label, value }) => (
                        <div key={label} className="flex flex-col gap-1 px-4 py-3 bg-gray-50 border border-[#E2E8F0] rounded-lg min-w-[140px]">
                            <span className="text-xs text-gray-500 font-medium">{label}:</span>
                            <span className="text-[15px] font-bold text-gray-800 font-mono">{value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CargoTab;